/**
 * The reference rule matcher (engine.md §8.2). Pure and synchronous; it DEFINES the semantics of the
 * condition vocabulary. The View->SQL compiler (engine/query/compile.ts) must agree with
 * `evaluate(...) === 'match'` on every message (property-tested by WP1).
 */

import type { EpochMs, Score } from '../contracts/ids.js';
import type { MatchModelScore, Script } from '../contracts/models.js';
import type {
  ConditionNode, MatchContext, MatchResult, MatchSnapshot, NumberOperator, QueryNode, RuleMatcher, TextOperator,
} from '../contracts/rules.js';
import { containsPhrase, foldText, tokens } from './text.js';

export const HOUR_MS = 3_600_000;
export const DAY_MS = 86_400_000;

// ── Completeness ───────────────────────────────────────────────────────────────────────────────

/** A condition with no values, blank text, a value with no tokens or a non-finite number. */
export function isCompleteCondition(node: ConditionNode): boolean {
  switch (node.field) {
    case 'folder':
    case 'source':
    case 'source-type':
    case 'topic':
    case 'tag':
      return Array.isArray(node.values) && node.values.length > 0 && node.values.every((v) => typeof v === 'string' && v !== '');
    case 'title':
    case 'author':
      return typeof node.value === 'string' && foldText(node.value) !== '';
    case 'body':
      return typeof node.value === 'string' && tokens(node.value).length > 0;
    case 'read':
      return node.value === 'read' || node.value === 'unread';
    case 'image':
      return node.value === 'yes' || node.value === 'no';
    case 'topic-score':
      return Number.isFinite(node.value) && (node.topic === undefined || (typeof node.topic === 'string' && node.topic !== ''))
        && (node.model === undefined || (typeof node.model === 'string' && node.model !== ''));
    case 'best-score':
      return Number.isFinite(node.value) && (node.model === undefined || (typeof node.model === 'string' && node.model !== ''));
    case 'hours':
      return Number.isFinite(node.value) && (node.unit === 'hours' || node.unit === 'days');
    case 'script':
      return Array.isArray(node.values) && node.values.length > 0 && node.values.every((v) => SCRIPTS.includes(v));
  }
}

const SCRIPTS: readonly Script[] = ['latin', 'cyrillic', 'other'];

/**
 * The vector a score condition tests (models.md §8.1). With `model`: that model's own row — `unknown` while
 * pending, nothing (`no-match`) when it never scored the message or failed; a tombstoned (withdrawn) row is
 * not in the snapshot. Without: the SHOWN scores. A row that is pending again after an earlier score keeps
 * its previous vector, but a condition naming the model still waits for the new one.
 */
function scoreSource(model: string | undefined, s: MatchSnapshot): { scores: Readonly<Record<string, Score>> | null; best: Score | null } | 'unknown' {
  if (model === undefined) return { scores: s.scores, best: s.bestScore };
  const row: MatchModelScore | undefined = s.modelScores?.find((m) => m.modelId === model);
  if (!row || row.state === 'failed') return { scores: null, best: null };
  if (row.state === 'pending') return 'unknown';
  return { scores: row.scores, best: row.bestScore };
}

// ── Primitive comparisons ──────────────────────────────────────────────────────────────────────

export function compareNumber(op: NumberOperator, left: number, right: number): boolean {
  switch (op) {
    case 'gte': return left >= right;
    case 'gt': return left > right;
    case 'lte': return left <= right;
    case 'lt': return left < right;
    case 'eq': return left === right;
  }
}

export function compareText(op: TextOperator, folded: string, valueFolded: string): boolean {
  switch (op) {
    case 'contains': return folded.includes(valueFolded);
    case 'not-contains': return !folded.includes(valueFolded);
    case 'equals': return folded === valueFolded;
    case 'not-equals': return folded !== valueFolded;
    case 'starts-with': return folded.startsWith(valueFolded);
    case 'ends-with': return folded.endsWith(valueFolded);
  }
}

function setMatch(op: 'in' | 'not-in' | 'all', have: readonly string[], values: readonly string[]): boolean {
  switch (op) {
    case 'in': return values.some((v) => have.includes(v));
    case 'not-in': return !values.some((v) => have.includes(v));
    case 'all': return values.every((v) => have.includes(v));
  }
}

const bool = (b: boolean): MatchResult => (b ? 'match' : 'no-match');

/** The unit of a dwell condition in milliseconds. */
export function dwellUnitMs(unit: 'hours' | 'days'): number {
  return unit === 'days' ? DAY_MS : HOUR_MS;
}

function evaluateCondition(node: ConditionNode, s: MatchSnapshot, ctx: MatchContext): MatchResult {
  if (!isCompleteCondition(node)) return 'no-match';
  switch (node.field) {
    case 'folder': return bool(setMatch(node.operator, [s.folderId], node.values));
    case 'source': return bool(setMatch(node.operator, [s.sourceId], node.values));
    case 'source-type': return bool(setMatch(node.operator, [s.sourceKind], node.values));
    case 'topic': return bool(setMatch(node.operator, s.topicIds, node.values));
    case 'tag': return bool(setMatch(node.operator, s.tagIds, node.values));
    case 'title': return bool(compareText(node.operator, s.titleFold, foldText(node.value)));
    case 'author': return bool(compareText(node.operator, s.authorFold, foldText(node.value)));
    case 'body': {
      if (s.bodyTokens === null) return 'unknown';
      const found = containsPhrase(s.bodyTokens, tokens(node.value));
      return bool(node.operator === 'contains' ? found : !found);
    }
    case 'read': return bool(node.value === 'read' ? s.read : !s.read);
    case 'image': return bool(node.value === 'yes' ? s.hasImage : !s.hasImage);
    case 'topic-score': {
      const src = scoreSource(node.model, s);
      if (src === 'unknown') return 'unknown';
      if (src.scores === null) return 'no-match';
      if (node.topic !== undefined) {
        const v = src.scores[node.topic];
        return bool(v !== undefined && compareNumber(node.operator, v, node.value));
      }
      return bool(Object.values(src.scores).some((v) => compareNumber(node.operator, v, node.value)));
    }
    case 'best-score': {
      const src = scoreSource(node.model, s);
      if (src === 'unknown') return 'unknown';
      return bool(src.best !== null && compareNumber(node.operator, src.best, node.value));
    }
    case 'script':
      return bool(setMatch(node.operator, [s.script ?? 'other'], node.values));
    case 'hours': {
      const unit = dwellUnitMs(node.unit);
      const elapsed = ctx.now - s.folderEnteredAt;
      if (node.operator === 'eq') return bool(Math.floor(elapsed / unit) === node.value);
      return bool(compareNumber(node.operator, elapsed, node.value * unit));
    }
  }
}

function evaluateNode(node: QueryNode, s: MatchSnapshot, ctx: MatchContext, root: boolean): MatchResult {
  if (node.type === 'condition') return evaluateCondition(node, s, ctx);
  if (node.children.length === 0) return root ? 'match' : 'no-match';
  let unknown = false;
  if (node.operator === 'all') {
    for (const child of node.children) {
      const r = evaluateNode(child, s, ctx, false);
      if (r === 'no-match') return 'no-match';
      if (r === 'unknown') unknown = true;
    }
    return unknown ? 'unknown' : 'match';
  }
  for (const child of node.children) {
    const r = evaluateNode(child, s, ctx, false);
    if (r === 'match') return 'match';
    if (r === 'unknown') unknown = true;
  }
  return unknown ? 'unknown' : 'no-match';
}

// ── Explanations ───────────────────────────────────────────────────────────────────────────────

const OP_SYMBOL: Record<NumberOperator, string> = { gte: '≥', gt: '>', lte: '≤', lt: '<', eq: '=' };
const TEXT_VERB: Record<TextOperator, string> = {
  contains: 'contains', 'not-contains': 'does not contain', equals: 'is', 'not-equals': 'is not',
  'starts-with': 'starts with', 'ends-with': 'ends with',
};
const FIELD_LABEL = { title: 'Title', author: 'Author', body: 'Body' } as const;
const SCRIPT_LABEL: Record<Script, string> = { latin: 'Latin', cyrillic: 'Cyrillic', other: 'other' };


/** A display name for an id (MatchContext.name), else the id itself. */
function named(ctx: MatchContext, kind: 'folder' | 'source' | 'topic' | 'tag', id: string): string {
  return ctx.name?.(kind, id) ?? id;
}

function list(values: readonly string[], ctx?: MatchContext, kind?: 'folder' | 'source' | 'topic' | 'tag'): string {
  return values.map((v) => (ctx && kind ? named(ctx, kind, v) : v)).join(', ');
}

/** One human-readable line for a condition that holds for this snapshot. */
export function describeCondition(node: ConditionNode, s: MatchSnapshot, ctx: MatchContext): string {
  switch (node.field) {
    case 'folder': return `Folder ${node.operator === 'in' ? 'is' : 'is not'} ${list(node.values, ctx, 'folder')}`;
    case 'source': return `Source ${node.operator === 'in' ? 'is' : 'is not'} ${list(node.values, ctx, 'source')}`;
    case 'source-type': return `Source type ${node.operator === 'in' ? 'is' : 'is not'} ${list(node.values)}`;
    case 'topic':
    case 'tag': {
      const label = node.field === 'topic' ? 'Topic' : 'Tag';
      const verb = node.operator === 'in' ? 'is one of' : node.operator === 'not-in' ? 'is none of' : 'includes all of';
      return `${label} ${verb} ${list(node.values, ctx, node.field)}`;
    }
    case 'title':
    case 'author':
    case 'body':
      return `${FIELD_LABEL[node.field]} ${TEXT_VERB[node.operator]} “${node.value}”`;
    case 'read': return node.value === 'read' ? 'Is read' : 'Is unread';
    case 'image': return node.value === 'yes' ? 'Has an image' : 'Has no image';
    case 'topic-score': {
      const src = scoreSource(node.model, s);
      const scores = src === 'unknown' ? null : src.scores;
      if (node.topic !== undefined) {
        const v: Score | undefined = scores?.[node.topic];
        const name = ctx.name?.('topic', node.topic) ?? null;
        return name !== null ? `${name} ${v ?? '—'} ${OP_SYMBOL[node.operator]} ${node.value}` : `Topic ${node.topic} score ${v ?? '—'} ${OP_SYMBOL[node.operator]} ${node.value}`;
      }
      return `A topic score ${OP_SYMBOL[node.operator]} ${node.value}`;
    }
    case 'best-score': {
      const src = scoreSource(node.model, s);
      const best = src === 'unknown' ? null : src.best;
      // The model a condition names is not in the sentence: a moved event carries it in detail.modelId.
      return `Best score ${best ?? '—'} ${OP_SYMBOL[node.operator]} ${node.value}`;
    }
    case 'script': return `Writing system ${node.operator === 'in' ? 'is' : 'is not'} ${node.values.map((v) => SCRIPT_LABEL[v]).join(', ')}`;
    case 'hours': {
      const unit = dwellUnitMs(node.unit);
      const elapsed = Math.floor((ctx.now - s.folderEnteredAt) / unit);
      return `In folder ${elapsed} ${node.unit} ${OP_SYMBOL[node.operator]} ${node.value}`;
    }
  }
}

function explainNode(node: QueryNode, s: MatchSnapshot, ctx: MatchContext, root: boolean, out: string[]): void {
  if (node.type === 'condition') {
    if (evaluateCondition(node, s, ctx) === 'match') out.push(describeCondition(node, s, ctx));
    return;
  }
  if (evaluateNode(node, s, ctx, root) !== 'match') return;
  if (node.operator === 'all') {
    for (const child of node.children) explainNode(child, s, ctx, false, out);
  } else {
    const first = node.children.find((c) => evaluateNode(c, s, ctx, false) === 'match');
    if (first) explainNode(first, s, ctx, false, out);
  }
}

// ── Time ───────────────────────────────────────────────────────────────────────────────────────

function collectDwell(node: QueryNode, out: Array<Extract<ConditionNode, { field: 'hours' }>>): void {
  if (node.type === 'condition') {
    if (node.field === 'hours' && isCompleteCondition(node)) out.push(node);
    return;
  }
  for (const child of node.children) collectDwell(child, out);
}

/** True when the tree contains at least one (complete) dwell condition. */
export function hasDwellCondition(node: QueryNode): boolean {
  const found: Array<Extract<ConditionNode, { field: 'hours' }>> = [];
  collectDwell(node, found);
  return found.length > 0;
}

export function createRuleMatcher(): RuleMatcher {
  return {
    evaluate(node, snapshot, ctx) {
      return evaluateNode(node, snapshot, ctx, true);
    },
    explain(node, snapshot, ctx) {
      const out: string[] = [];
      explainNode(node, snapshot, ctx, true, out);
      return out;
    },
    nextDueAt(node, snapshot, ctx) {
      if (evaluateNode(node, snapshot, ctx, true) === 'match') return null;
      const dwell: Array<Extract<ConditionNode, { field: 'hours' }>> = [];
      collectDwell(node, dwell);
      // Results only change at these instants; test each future one in order.
      const candidates = new Set<number>();
      for (const c of dwell) {
        const at = snapshot.folderEnteredAt + c.value * dwellUnitMs(c.unit);
        candidates.add(at);
        candidates.add(at + 1);
      }
      const future = [...candidates].filter((t) => t > ctx.now).sort((a, b) => a - b);
      for (const t of future) {
        if (evaluateNode(node, snapshot, { now: t as EpochMs }, true) === 'match') return t as EpochMs;
      }
      return null;
    },
  };
}

/** A shared instance; the matcher is stateless. */
export const ruleMatcher: RuleMatcher = createRuleMatcher();

